/** The studio's plain words: times, the session's place in a series, and where the host goes afterwards. */
import { daysUntil, labelParts, liveDayWord, liveChipLabel } from "../../shared/live-time";
import { money } from "../../shared/plans";
import type { LiveSessionView, LiveStudio, LiveTopupNotice, StudioLive, WebinarLive } from "../../shared/types";
import { dayMid, timeOf } from "../board/series";
import { sessionReportPath } from "../views/rooms/liveWords";
import { amount } from "../billing/format";
import { inApp } from "../lib/inApp";

const pad = (n: number) => String(n).padStart(2, "0");

/** "09:12", or "1:09:12" past an hour. */
export function clockWords(totalSec: number): string {
  const s = Math.max(0, Math.floor(totalSec));
  const h = Math.floor(s / 3600);
  const mm = pad(Math.floor((s % 3600) / 60));
  return h > 0 ? `${h}:${mm}:${pad(s % 60)}` : `${mm}:${pad(s % 60)}`;
}

/** "7:02 PM" in the live's own zone. */
export function timeIn(iso: string, timezone: string): string {
  return labelParts(iso, timezone, "long")?.time ?? "";
}

const MINUTE_MS = 60_000;
const HOUR_MS = 60 * MINUTE_MS;

/** The honest time on the phone's nav: "Tomorrow, 6:00 PM", "In 3 days", "In 42 min", or the date past a week. */
export function studioChip(live: Pick<StudioLive, "startsAt" | "timezone">, nowMs: number): string {
  const left = Date.parse(live.startsAt) - nowMs;
  if (!Number.isFinite(left)) return "";
  if (left <= 0) return "Starts now";
  if (left < HOUR_MS) return `In ${Math.ceil(left / MINUTE_MS)} min`;
  const days = daysUntil(live.startsAt, live.timezone, nowMs);
  const parts = labelParts(live.startsAt, live.timezone, "short");
  if (days === null || !parts) return liveChipLabel(live.startsAt, live.timezone);
  if (days <= 0) return `Today, ${parts.time}`;
  if (days === 1) return `Tomorrow, ${parts.time}`;
  if (days < 7) return `In ${days} days`;
  return `${parts.month} ${parts.day}, ${parts.time}`;
}

/** "The room opens at 5:50 PM", with the day when it is not today: "The room opens tomorrow at 5:50 PM". */
export function opensLine(live: Pick<StudioLive, "opensAt" | "timezone">, nowMs: number): string {
  const opens = Date.parse(live.opensAt);
  const at = timeIn(live.opensAt, live.timezone);
  if (!Number.isFinite(opens) || !at) return "";
  const days = daysUntil(live.opensAt, live.timezone, nowMs);
  const day = days === 1 ? "tomorrow" : days !== null && days > 1 ? liveDayWord(live.opensAt, live.timezone, nowMs, "short") : "";
  return `The room ${opens <= nowMs ? "opened" : "opens"}${day ? ` ${day}` : ""} at ${at}`;
}

/** What the ended screen says about the replay; `justEnded` is true when this tab ended the live a moment ago. */
export function replayLine(summary: Pick<WebinarLive, "replay"> | null, justEnded: boolean): string {
  if (!summary) return justEnded ? "The replay is building." : "";
  const replay = summary.replay;
  if (!replay.recorded) return "This live was not recorded.";
  if (replay.playable) return "The replay is ready.";
  if (replay.videoState === "error") return "The replay didn't finish.";
  return "The replay is building.";
}

/** The host-only note when live hours ran out and more were added. */
export function topupWords(notice: LiveTopupNotice, timezone: string): { title: string; body: string } {
  const at = timeIn(new Date(notice.at * 1000).toISOString(), timezone);
  return {
    title: `${amount(notice.hours)} hours of live attendance added`,
    body: `Your live attendance ran out${at ? ` at ${at}` : ""}.${inApp() ? "" : ` We charged ${money(notice.amount_cents, notice.currency)}.`}${
      notice.left_hours > 0 ? ` ${amount(notice.left_hours)} hours left this month.` : ""
    }`,
  };
}

/** The one button at the door: what it says while the camera is starting, while it works, and when it is ready. */
export function goLabel({ waitingCamera, busy, rejoin }: { waitingCamera: boolean; busy: boolean; rejoin: boolean }): string {
  if (busy) return rejoin ? "Going in" : "Going live";
  if (waitingCamera) return "Waiting for the camera";
  return rejoin ? "Join the live" : "Go live";
}

/** What kind of live just ended: a room's, a quick live, or a circle's call. */
export type EndKind = "room" | "quick" | "circle";

/** "3 came: Joe, Grace and Elena." for a quick live; with more than three, the rest as a count. */
export function cameLine(came: number, names: readonly string[]): string {
  if (came === 0) return "No one came.";
  const first = names.slice(0, 3);
  if (first.length === 0) return `${came} came.`;
  const rest = came - first.length;
  const list = rest > 0 ? `${first.join(", ")} and ${rest} more` : first.length > 1 ? `${first.slice(0, -1).join(", ")} and ${first.at(-1)}` : (first[0] ?? "");
  return `${came} came: ${list}.`;
}

/** The replay note on the page after a quick live or a circle's call: where the replay goes when it is ready. */
export function replayGoes(kind: EndKind, replay: string, came: number, members: number): string {
  if (replay !== "The replay is building.") return replay;
  if (kind === "quick" && came > 0) return `${replay} It goes to the ${came} who came the moment it is ready.`;
  if (kind === "circle") return `${replay} It goes into the circle for all ${members} the moment it is ready.`;
  return replay;
}

/** Where "See how it went" goes: a series session's own report, or a one-off's page. */
export function howItWentPath(info: Pick<LiveStudio, "webinarId" | "sessionId" | "series">): string {
  return info.series ? sessionReportPath(info.webinarId, info.sessionId) : `/rooms/${info.webinarId}`;
}

/**
 * Which session this is, for a series: "Session 3 of 10 · Every Thu" and its
 * topic. A one-off says only its topic, when it has one.
 */
export function sessionLine(info: Pick<LiveStudio, "series" | "topic">): string {
  const place = info.series
    ? [
        info.series.total === null ? `Session ${info.series.seq}` : `Session ${info.series.seq} of ${info.series.total}`,
        info.series.chip.slice(0, info.series.chip.lastIndexOf(" · ")),
      ].join(" · ")
    : "";
  return [place, info.topic ?? ""].filter(Boolean).join(" · ");
}

/** "Next session: Thursday, Oct 1 · 7:00 PM", once a session of a series has ended. */
export function nextSessionLine(next: LiveSessionView | null): string {
  if (!next) return "That was the last session of the series.";
  return `Next session: ${dayMid(next.startsAt, next.timezone)} · ${timeOf(next.startsAt, next.timezone)}`;
}
